import * as React from 'react';

import { cn } from '@rematter/ui-web';
import {
  LucideBot,
  LucideBox,
  LucideBuilding2,
  LucideCoins,
  LucideFiles,
  LucideStore,
  LucideTruck,
  LucideUsers,
} from 'lucide-react';

import { CoreEventsView } from './_components/core';
import { useAuditLogLocation } from './_state/auditLogModule.state';
import { MenuItemGroup } from './_types';

import { AssistantEventsView } from '@/assistant/_components/_logs';
import { CrmEventsView } from '@/crm/_components/_logs';
import { FilesEventsView } from '@/files/_components/_logs';
import { FinanceEventsView } from '@/finance/_components/_logs';
import { InventoryEventsView } from '@/inventory/_components/_logs';
import { TradeEventsView } from '@/trade/_components/_logs';
import { TransportationEventsView } from '@/transportation/_components/_logs';

export const auditLogRouteGroups = [
  {
    groupTitleKey: 'modules',
    items: [
      {
        pattern: '/trade',
        labelKey: 'trade',
        icon: LucideStore,
        component: TradeEventsView,
        disabled: false,
      },
      {
        pattern: '/finance',
        labelKey: 'finance',
        icon: LucideCoins,
        component: FinanceEventsView,
        disabled: false,
      },
      {
        pattern: '/transportation',
        labelKey: 'transportation',
        icon: LucideTruck,
        component: TransportationEventsView,
        disabled: false,
      },
      {
        pattern: '/crm',
        labelKey: 'crm',
        icon: LucideUsers,
        component: CrmEventsView,
        disabled: false,
      },
      {
        pattern: '/core',
        labelKey: 'core',
        icon: LucideBuilding2,
        component: CoreEventsView,
        disabled: false,
      },
      {
        pattern: '/inventory',
        labelKey: 'inventory',
        icon: LucideBox,
        component: InventoryEventsView,
        disabled: false,
      },
      {
        pattern: '/assistant',
        labelKey: 'assistant',
        icon: LucideBot,
        component: AssistantEventsView,
        disabled: false,
      },
      {
        pattern: '/files',
        labelKey: 'files',
        icon: LucideFiles,
        component: FilesEventsView,
        disabled: false,
      },
    ],
  },
] as const satisfies MenuItemGroup[];

type Flatten<T> = T extends string
  ? T
  : T extends (infer U)[]
    ? Flatten<U>
    : never;

type RouteToTemplate<T extends string> =
  T extends `${infer Start}:${infer _Param}/${infer Rest}`
    ? `${Start}${string}/${RouteToTemplate<Rest>}`
    : T extends `${infer Start}:${infer _Param}`
      ? `${Start}${string}`
      : T;

export type AuditLogPath =
  | RouteToTemplate<
      Flatten<(typeof auditLogRouteGroups)[number]['items'][number]['pattern']>
    >
  | '/';

export const AuditLogLink = ({
  href,
  className,
  ...props
}: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: AuditLogPath;
}) => {
  const [, setLocation] = useAuditLogLocation();

  return (
    <a
      {...props}
      className={cn('@AuditLogLink', className)}
      href={href as never}
      onClick={(e) => {
        e.preventDefault();
        setLocation(href as AuditLogPath);
        props.onClick?.(e);
      }}
    />
  );
};
